import React, { useState, useContext, useRef, createContext, useEffect, useCallback } from "react"

import "./QuizCard.scss"
import {
  quizDesktopMinInnerHeight,
  quizMobileMinInnerHeight
} from "@style/sd.scss"

import { useLayout } from "../layout"

interface ICardContext {
  maxInnerHeight: number
  fullyFitsIntoCard(el: HTMLElement): boolean
  recalcInnerHeight(): void
}

const QuizCardContext = createContext<ICardContext>({
  maxInnerHeight: 0,
  fullyFitsIntoCard: () => false,
  recalcInnerHeight: () => {},
})
QuizCardContext.displayName = "QuizCardContext"

export const useQuizCard = () => useContext(QuizCardContext)

const QuizCard: React.FC<React.PropsWithChildren<{Overlay?: React.ReactNode}>> = ({children, Overlay}) => {
  const layout = useLayout()
  const innerContentRef = useRef<HTMLDivElement>()
  const maxInnerHeight = layout.type === "mobile" ? quizMobileMinInnerHeight : quizDesktopMinInnerHeight
  const [ innerHeight, setInnerHeight ] = useState(maxInnerHeight)
  const innerHeightRef = useRef(innerHeight)
  innerHeightRef.current = innerHeight

  const recalcInnerHeight = useCallback(() => {
    if (!innerContentRef.current) { return }
    let newInnerHeight = innerHeightRef.current

    const targetInnerHeight = layout.type === "desktop"
      ? quizDesktopMinInnerHeight
      : quizMobileMinInnerHeight

    const style = window.getComputedStyle(innerContentRef.current)
    newInnerHeight = parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10) + innerContentRef.current.clientHeight
    if (newInnerHeight < targetInnerHeight) { newInnerHeight = targetInnerHeight }
    if (newInnerHeight !== innerHeightRef.current) { setInnerHeight(newInnerHeight) }
  }, [layout.type])
  const recalcInnerHeightRef = useRef(recalcInnerHeight)
  recalcInnerHeightRef.current = recalcInnerHeight

  useEffect(() => { // ----------- every time this render function is called
    recalcInnerHeightRef.current()
  })

  useEffect(() => { // ----------- ONCE -> recalc height every sec (because async font-loading can mess with layout)
    const interval = window.setInterval(() => {
      recalcInnerHeightRef.current()
    }, 1000)

    return () => { // cleanup
      window.clearInterval(interval)
    }
  }, [])

  const fullyFitsIntoCard = (el: HTMLElement) => {
    const style = window.getComputedStyle(el)
    return (parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10) + el.clientHeight) <= maxInnerHeight
  }

  return (
    <QuizCardContext.Provider value={{maxInnerHeight, fullyFitsIntoCard, recalcInnerHeight}}>
      <div className="neo__quizCard">
        <div style={{height: `${innerHeight}px`}} className="neo__quizCard__inner">
          <div ref={(ref) => ref && (innerContentRef.current = ref)} className="neo__quizCard__innerContentHolder">
            {children}
          </div>
          {Overlay}
        </div>
      </div>
    </QuizCardContext.Provider>
  )
}

export default QuizCard
